import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function formatoPesos(value) {
    return '$ ' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function FormatoFechaSinHora(value) {
    const fecha = new Date(value);
    return format(fecha, 'dd-MMMM-yyyy', { locale: es }).toUpperCase();
}

function FormatoFechaConHora(value) {
    const fecha = new Date(value);
    return format(fecha, "dd-MMMM-yyyy HH:mm:ss", { locale: es }).toUpperCase();
}

function calcularAnchoProporcional(base64, alturaFija) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const ancho = img.naturalWidth && img.naturalHeight
                ? (img.naturalWidth * alturaFija) / img.naturalHeight
                : 50;
            resolve(ancho);
        };
        img.onerror = () => resolve(50);
        img.src = base64;
    });
}

function detectarFormatoImagen(base64) {
    if (base64.startsWith('data:image/png'))  return 'PNG';
    if (base64.startsWith('data:image/jpeg')) return 'JPEG';
    if (base64.startsWith('data:image/jpg'))  return 'JPEG';
    if (base64.startsWith('data:image/gif'))  return 'GIF';
    if (base64.startsWith('data:image/webp')) return 'WEBP';
    return 'PNG';
}

// Dibuja una sección (cabecera de grupo + filas de detalle) en una columna
function dibujarSeccion(doc, titulo, items, sumaTotal, color, xIzq, xDer, y) {
    const anchoSeccion = xDer - xIzq;

    // Barra de cabecera de sección
    doc.setFillColor(color);
    doc.rect(xIzq, y, anchoSeccion, 7, 'F');
    doc.setTextColor('#ffffff');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(titulo, xIzq + 3, y + 5, { align: 'left' });
    doc.text(formatoPesos(sumaTotal), xDer - 2, y + 5, { align: 'right' });

    y += 9;

    // Filas de detalle
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor('#000000');

    const padding = 1.5;

    for (let a of items) {
        const desc_ = doc.splitTextToSize(a.descripcion || '', anchoSeccion - 30);
        const descH = doc.getTextDimensions(desc_).h;

        // Texto con padding superior
        doc.text(desc_, xIzq + 3, y + padding, { align: 'left' });
        doc.text(
            a.importe != null && a.importe !== 0 ? formatoPesos(a.importe) : '',
            xDer - 2, y + padding, { align: 'right' }
        );

        // Avanzar y ANTES de la línea para que quede debajo del texto
        y += padding + descH + padding;

        // Línea separadora debajo del texto
        doc.setDrawColor('#dddddd');
        // doc.setLineWidth(0.1);
        // doc.line(xIzq, y, xDer, y);
    }

    return y + 2;
}

const EstadoSituacionFinancieraPDF = async (
    objActivoCirculante, objActivoFijo, objActivoDiferido,
    objPasivoCirculante, objPasivoLargoPlazo, objPasivoDiferido,
    objCapital, objEmpresa, fechaI, fechaF
) => {
    return new Promise(async (resolve, reject) => {
        try {
            const doc = new jsPDF({ format: 'letter', orientation: 'landscape' });

            const color               = objEmpresa?.color || '#662e91';
            const logoBase64          = objEmpresa?.logo?.base64 || '';
            const fechaHoraGeneracion = FormatoFechaConHora(new Date());

            // Landscape: 279mm ancho x 216mm alto
            const pageW     = doc.internal.pageSize.getWidth();
            const pageH     = doc.internal.pageSize.getHeight();
            const margen    = 12;
            const mitad     = pageW / 2;

            // Columna Activo:        xIzq=12  xDer=mitad-4
            // Columna Pasivo+Capital: xIzq=mitad+4  xDer=pageW-12
            const xActivoIzq  = margen;
            const xActivoDer  = mitad - 4;
            const xPasivoIzq  = mitad + 4;
            const xPasivoDer  = pageW - margen;
            const xDer        = pageW - margen;

            // ── CABECERA ──────────────────────────────────────────────────
            let y = 14;

            // Logo
            if (logoBase64 !== '') {
                const alturaLogo = 18;
                const formatoImg = detectarFormatoImagen(logoBase64);
                const anchoLogo  = await calcularAnchoProporcional(logoBase64, alturaLogo);
                doc.addImage(logoBase64, formatoImg, margen, y, anchoLogo, alturaLogo);
            }

            // Título + datos empresa (derecha)
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('Estado de Situación Financiera', xDer, y, { align: 'right' });

            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text((objEmpresa?.rfc || '') + ' | ' + (objEmpresa?.nombre || ''), xDer, y += 6, { align: 'right' });

            const periodo = 'PERIODO DEL ' + FormatoFechaSinHora(fechaI) + ' AL ' + FormatoFechaSinHora(fechaF);
            doc.text(periodo, xDer, y += 5, { align: 'right' });

            y += 8;

            // Títulos de columnas
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(color);
            doc.text('ACTIVO', (xActivoIzq + xActivoDer) / 2, y, { align: 'center' });
            doc.text('PASIVO Y CAPITAL', (xPasivoIzq + xPasivoDer) / 2, y, { align: 'center' });

            // Línea divisoria central
            doc.setDrawColor(color);
            doc.setLineWidth(0.4);
            doc.line(mitad, y - 4, mitad, pageH - 20);

            y += 5;

            // ── COLUMNA ACTIVO ────────────────────────────────────────────
            const sumaActivoCirculante = objActivoCirculante.reduce((a, b) => a + b.importe, 0);
            const sumaActivoFijo       = objActivoFijo.reduce((a, b) => a + b.importe, 0);
            const sumaActivoDiferido   = objActivoDiferido.reduce((a, b) => a + b.importe, 0);

            let yActivo = y;
            yActivo = dibujarSeccion(doc, 'Activo Circulante', objActivoCirculante, sumaActivoCirculante, color, xActivoIzq, xActivoDer, yActivo);
            yActivo = dibujarSeccion(doc, 'Activo Fijo',       objActivoFijo,       sumaActivoFijo,       color, xActivoIzq, xActivoDer, yActivo);
            yActivo = dibujarSeccion(doc, 'Activo Diferido',   objActivoDiferido,   sumaActivoDiferido,   color, xActivoIzq, xActivoDer, yActivo);

            // ── COLUMNA PASIVO + CAPITAL ──────────────────────────────────
            const sumaPasivoCirculante  = objPasivoCirculante.reduce((a, b) => a + b.importe, 0);
            const sumaPasivoLargoPlazo  = objPasivoLargoPlazo.reduce((a, b) => a + b.importe, 0);
            const sumaPasivoDiferido    = objPasivoDiferido.reduce((a, b) => a + b.importe, 0);
            const sumaCapital           = objCapital.reduce((a, b) => a + b.importe, 0);

            let yPasivo = y;
            yPasivo = dibujarSeccion(doc, 'Pasivo Circulante',  objPasivoCirculante, sumaPasivoCirculante, color, xPasivoIzq, xPasivoDer, yPasivo);
            yPasivo = dibujarSeccion(doc, 'Pasivo Largo Plazo', objPasivoLargoPlazo, sumaPasivoLargoPlazo, color, xPasivoIzq, xPasivoDer, yPasivo);
            yPasivo = dibujarSeccion(doc, 'Pasivo Diferido',    objPasivoDiferido,   sumaPasivoDiferido,   color, xPasivoIzq, xPasivoDer, yPasivo);
            yPasivo = dibujarSeccion(doc, 'Capital',            objCapital,          sumaCapital,          color, xPasivoIzq, xPasivoDer, yPasivo);

            // ── TOTALES FINALES ───────────────────────────────────────────
            const sumaActivos         = sumaActivoCirculante + sumaActivoFijo + sumaActivoDiferido;
            const sumaPasivosYCapital = sumaPasivoCirculante + sumaPasivoLargoPlazo + sumaPasivoDiferido + sumaCapital;
            const yTotales            = pageH - 22;

            // Barra total Activo
            doc.setFillColor(color);
            doc.rect(xActivoIzq, yTotales, xActivoDer - xActivoIzq, 8, 'F');
            doc.setTextColor('#ffffff');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.text('TOTAL ACTIVO:', xActivoIzq + 3, yTotales + 5.5, { align: 'left' });
            doc.text(formatoPesos(sumaActivos), xActivoDer - 2, yTotales + 5.5, { align: 'right' });

            // Barra total Pasivo + Capital
            doc.setFillColor(color);
            doc.rect(xPasivoIzq, yTotales, xPasivoDer - xPasivoIzq, 8, 'F');
            doc.text('TOTAL PASIVO + CAPITAL:', xPasivoIzq + 3, yTotales + 5.5, { align: 'left' });
            doc.text(formatoPesos(sumaPasivosYCapital), xPasivoDer - 2, yTotales + 5.5, { align: 'right' });

            // ── PIE DE PÁGINA ─────────────────────────────────────────────
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8);
                doc.setTextColor(150);

                doc.setDrawColor(color);
                doc.setLineWidth(0.3);
                doc.line(margen, pageH - 12, pageW - margen, pageH - 12);

                doc.text('Generado: ' + fechaHoraGeneracion, margen, pageH - 6, { align: 'left' });
                doc.text('Documento generado por ContaGo', pageW / 2, pageH - 6, { align: 'center' });
                doc.text('Página ' + i + ' de ' + totalPages, pageW - margen, pageH - 6, { align: 'right' });
            }

            const base64 = doc.output('datauristring');
            resolve(base64);
        } catch (error) {
            console.error(error);
            reject(error);
        }
    });
};

export { EstadoSituacionFinancieraPDF };